import { useMemo } from 'react'
import useMediaQuery from '@app/util/useMediaQuery'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import {
  municipalityPageLayoutTabs,
  municipalityTabs,
} from '@app/pages/discover/regions/constants'
import type { MunicipalityFeatures } from '@schema/featureToggle'
import { useMunicipalityLayoutTabAvailability } from '@app/pages/discover/regions/municipalityPage/useMunicipalityLayoutTabAvailability'
import { isMunicipalityLayoutTabAvailable } from '@app/pages/discover/regions/municipalityPage/isMunicipalityLayoutTabAvailable'
import { pages } from '@app/util/links'
import { navigate } from 'wouter/use-browser-location'
import { useRegionalHeaderScroll } from '@app/pages/discover/regions/regionalPage/RegionalHeaderScrollContext'
import { GeographyNavBarShell } from '@app/components/blocks/navbar/shared/GeographyNavBarShell'

const layoutTabKeys = Object.values(municipalityPageLayoutTabs)

export type MunicipalityNavBarProps = {
  municipality: MunicipalityViewModel
  tab: MunicipalityFeatures
}

export const MunicipalityNavBar = ({
  municipality,
  tab,
}: MunicipalityNavBarProps) => {
  const isMobile = useMediaQuery()
  const [language] = useLanguage()
  const translations = i18n.languages[language].app.regionPage
  const { context } = useMunicipalityLayoutTabAvailability(municipality)
  const { showRegionName } = useRegionalHeaderScroll()
  const isOverviewTab = tab === municipalityTabs.overview
  const shouldShowEntityName = !isOverviewTab || showRegionName

  const tabs = useMemo(
    () =>
      layoutTabKeys.map((key) => ({
        value: key,
        label: translations[key],
        isVisible: isMunicipalityLayoutTabAvailable(municipality, key, context),
      })),
    [translations, municipality, context],
  )

  const handleSelectTab = (key: MunicipalityFeatures) => {
    navigate(pages.municipalityPageNew(municipality.slug, key))
  }

  return (
    <GeographyNavBarShell
      isMobile={isMobile}
      shouldShowEntityName={shouldShowEntityName}
      entityName={municipality.name}
      homePath={pages.municipalityPageNew(municipality.slug)}
      tab={tab}
      tabs={tabs}
      onSelectTab={(key) => handleSelectTab(key as MunicipalityFeatures)}
    />
  )
}
